import { isRecord } from 'lib/object/objectUtils'
import {
  readTranslatedString,
  readTrimmedString,
  readTrimmedStringArray,
} from 'loyalty/lib/labPayloadUtils'

import {
  LoyaltyOffersHubOfferTag,
  LoyaltyOffersHubUrgencyType,
} from './constants'
import type {
  LoyaltyOffersHubHeroCarouselItem,
  LoyaltyOffersHubOffer,
} from './types'

const LOYALTY_OFFERS_HUB_OFFER_TAG_VALUES = new Set<string>(Object.values(LoyaltyOffersHubOfferTag))
const LOYALTY_OFFERS_HUB_URGENCY_TYPE_VALUES = new Set<string>(Object.values(LoyaltyOffersHubUrgencyType))

function isActiveForRegion(source: Record<string, unknown>, regionCode: string, now: Date): boolean {
  if (source.isActive !== true) return false

  const eligibleRegions = readTrimmedStringArray(source.eligibleRegions)
  if (!eligibleRegions || (eligibleRegions.length > 0 && !eligibleRegions.includes(regionCode))) return false

  const startAt = readTrimmedString(source.startAt)
  const endAt = readTrimmedString(source.endAt)
  if (!startAt || !endAt) return false

  const startTime = new Date(startAt).getTime()
  const endTime = new Date(endAt).getTime()
  const nowTime = now.getTime()

  if (Number.isNaN(startTime) || Number.isNaN(endTime)) return false
  return startTime <= nowTime && nowTime <= endTime
}

function isLoyaltyOffersHubOfferTag(value: unknown): value is LoyaltyOffersHubOfferTag {
  return typeof value === 'string' && LOYALTY_OFFERS_HUB_OFFER_TAG_VALUES.has(value)
}

function isLoyaltyOffersHubUrgencyType(value: unknown): value is LoyaltyOffersHubUrgencyType {
  return typeof value === 'string' && LOYALTY_OFFERS_HUB_URGENCY_TYPE_VALUES.has(value)
}

export function mapLoyaltyHeroCarouselItem(
  value: unknown,
  regionCode: string,
  now: Date,
): LoyaltyOffersHubHeroCarouselItem | undefined {
  if (!isRecord(value) || !isActiveForRegion(value, regionCode, now)) return undefined

  const label = readTranslatedString(value, 'label')
  const imageId = readTrimmedString(value.imageId)
  const logoImageId = readTrimmedString(value.logoImageId)
  const title = readTranslatedString(value, 'title')
  const description = readTranslatedString(value, 'description')
  const ctaLabel = readTranslatedString(value, 'ctaLabel')
  const ctaUrl = readTrimmedString(value.ctaUrl)
  const startAt = readTrimmedString(value.startAt)
  const endAt = readTrimmedString(value.endAt)

  if (!label || !imageId || !title || !description || !ctaLabel || !ctaUrl || !startAt || !endAt) return undefined

  return {
    label,
    imageId,
    logoImageId,
    title,
    description,
    ctaLabel,
    ctaUrl,
    startAt,
    endAt,
  }
}

export function mapLoyaltyOffer(
  value: unknown,
  regionCode: string,
  now: Date,
): LoyaltyOffersHubOffer | undefined {
  if (!isRecord(value) || !isActiveForRegion(value, regionCode, now)) return undefined

  const offerTag = value.offerTag
  const imageId = readTrimmedString(value.imageId)
  const title = readTranslatedString(value, 'title')
  const description = readTranslatedString(value, 'description')
  const ctaLabel = readTranslatedString(value, 'ctaLabel')
  const ctaUrl = readTrimmedString(value.ctaUrl)
  const urgencyType = value.urgencyType
  const startAt = readTrimmedString(value.startAt)
  const endAt = readTrimmedString(value.endAt)

  if (
    !isLoyaltyOffersHubOfferTag(offerTag) ||
    !imageId ||
    !title ||
    !description ||
    !ctaLabel ||
    !ctaUrl ||
    !isLoyaltyOffersHubUrgencyType(urgencyType) ||
    !startAt ||
    !endAt
  ) return undefined

  const brandName = readTrimmedString(value.brandName)
  const industry = readTrimmedString(value.industry)
  const industryLabel = readTrimmedString(value['industry-i18n'])

  if (offerTag === LoyaltyOffersHubOfferTag.PARTNER_OFFER) {
    if (!brandName || !industry || !industryLabel) return undefined
  }

  return {
    offerTag,
    brandName,
    imageId,
    title,
    description,
    ctaLabel,
    ctaUrl,
    urgencyType,
    industry,
    industryLabel,
    startAt,
    endAt,
  }
}
